/**
 * Ordering for the web build versions CI stamps: `X.Y.Z` on stable and
 * `X.Y.Z-dev.<sha8>` on dev. Only a strictly newer deployed build prompts a
 * refresh, so a rollback or a malformed file never nags anyone.
 *
 * Identical in frontend, admin-portal and superadmin-portal.
 */

const BUILD_VERSION_PATTERN = /^v?(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?$/;

export interface BuildVersion {
  core: [number, number, number];
  prerelease: string | null;
}

export function parseBuildVersion(rawVersion: string | null | undefined): BuildVersion | null {
  const match = rawVersion?.trim().match(BUILD_VERSION_PATTERN);
  if (!match) {
    return null;
  }
  return {
    core: [Number(match[1]), Number(match[2]), Number(match[3])],
    prerelease: match[4] ?? null,
  };
}

/** Reads the version half of a `/version.txt` body ("<version> <target>"). */
export function parseVersionFile(fileBody: string): BuildVersion | null {
  const [firstToken] = fileBody.trim().split(/\s+/);
  return parseBuildVersion(firstToken);
}

function compareCore(left: BuildVersion, right: BuildVersion): number {
  for (let index = 0; index < left.core.length; index += 1) {
    const difference = left.core[index] - right.core[index];
    if (difference !== 0) {
      return difference;
    }
  }
  return 0;
}

/**
 * True when `deployed` is a strictly newer build than `running`.
 *
 * Same X.Y.Z: a release supersedes its own pre-release (`0.0.10` over
 * `0.0.10-dev.ab12cd34`). Two different dev builds of the same X.Y.Z differ
 * only by commit hash, which has no order; the deployed one is newer by
 * definition because it replaced the running one on this site.
 */
export function isNewerBuild(deployed: BuildVersion | null, running: BuildVersion | null): boolean {
  if (!deployed || !running) {
    return false;
  }
  const coreDifference = compareCore(deployed, running);
  if (coreDifference !== 0) {
    return coreDifference > 0;
  }
  if (deployed.prerelease === running.prerelease) {
    return false;
  }
  if (deployed.prerelease === null) {
    return true;
  }
  return running.prerelease !== null;
}
